/**
 * Migration Helper - Run Test Classification Migration
 * This can be called from the browser console or integrated into admin pages
 */

import { getFirestore, collection, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { firebaseApp } from '../firebase';

export interface MigrationProgress {
  current: number;
  total: number;
  status: 'idle' | 'processing' | 'completed' | 'error';
}

export interface MigrationResult {
  success: number;
  failed: number;
  skipped: number;
  errors: string[];
}

const classifyAndTagTest = async (jobTitle: string, jobDescription: string, testData: any) => {
  try {
    const response = await fetch(
      'https://us-central1-vetriaai-mvp.cloudfunctions.net/classifyAndTagTest',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ jobTitle, jobDescription, testData }),
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP error! status: ${response.status}`);
    }

    if (result.success && result.classification) {
      return result.classification;
    }

    throw new Error('Invalid response from classification service');
  } catch (error: any) {
    throw error;
  }
};

export const runMigration = async (
  onProgress?: (progress: MigrationProgress) => void,
  onResult?: (result: MigrationResult) => void,
  onLog?: (message: string, type: 'info' | 'success' | 'error' | 'warning') => void
): Promise<MigrationResult> => {
  const log = (message: string, type: 'info' | 'success' | 'error' | 'warning' = 'info') => {
    if (onLog) onLog(message, type);
  };

  log('🚀 Starting test migration...', 'info');

  const result: MigrationResult = {
    success: 0,
    failed: 0,
    skipped: 0,
    errors: [],
  };

  try {
    const db = getFirestore(firebaseApp);
    const testsSnapshot = await getDocs(collection(db, 'hrTests'));
    const tests = testsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    log(`📊 Found ${tests.length} tests in database`, 'info');

    if (onProgress) {
      onProgress({ current: 0, total: tests.length, status: 'processing' });
    }

    for (let i = 0; i < tests.length; i++) {
      const test = tests[i] as any;
      const progress = `[${i + 1}/${tests.length}]`;

      try {
        // Skip if already classified
        if (test.isTechnical !== undefined && test.tags && test.tags.length > 0) {
          log(`⏭️  ${progress} Skipped: ${test.jobTitle || 'Untitled'} (already classified)`, 'warning');
          result.skipped++;

          if (onProgress) {
            onProgress({ current: i + 1, total: tests.length, status: 'processing' });
          }
          continue;
        }

        log(`⚙️  ${progress} Processing: ${test.jobTitle || 'Untitled'}...`, 'info');

        // Get job description
        let jobDescription = '';
        if (test.jobId && !test.jobId.startsWith('preset_')) {
          try {
            const jobDocRef = doc(db, 'hrJobs', test.jobId);
            const jobDocSnap = await getDoc(jobDocRef);
            if (jobDocSnap.exists()) {
              jobDescription = jobDocSnap.data().jobDescription || '';
            }
          } catch (error) {
            log(`⚠️  Warning: Could not fetch job description for ${test.jobTitle}`, 'warning');
          }
        }

        // Classify the test
        const classification = await classifyAndTagTest(
          test.jobTitle || 'Untitled Test',
          jobDescription,
          test.testData || {}
        );

        // Update the test document
        await updateDoc(doc(db, 'hrTests', test.id), {
          isTechnical: classification.isTechnical,
          testType: classification.testType,
          domain: classification.domain,
          subDomains: classification.subDomains || [],
          tags: classification.tags || [],
          primarySkills: classification.primarySkills || [],
          difficulty: classification.difficulty,
          migratedAt: new Date(),
        });

        log(
          `✅ ${progress} Success: ${test.jobTitle || 'Untitled'} | Type: ${
            classification.isTechnical ? 'Technical' : 'Non-Technical'
          } | Tags: ${classification.tags.length}`,
          'success'
        );
        result.success++;

      } catch (error: any) {
        log(`❌ ${progress} Failed: ${test.jobTitle || 'Untitled'} - ${error.message}`, 'error');
        result.failed++;
        result.errors.push(`${test.jobTitle}: ${error.message}`);
      }

      if (onProgress) {
        onProgress({ current: i + 1, total: tests.length, status: 'processing' });
      }

      // Add delay to avoid rate limiting
      if (i < tests.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    log('\n📈 MIGRATION SUMMARY', 'info');
    log(`✅ Successfully migrated: ${result.success}`, 'success');
    log(`⏭️  Skipped (already classified): ${result.skipped}`, 'warning');
    log(`❌ Failed: ${result.failed}`, 'error');

    if (result.errors.length > 0) {
      log('\n⚠️  ERRORS:', 'error');
      result.errors.forEach((error, index) => {
        log(`${index + 1}. ${error}`, 'error');
      });
    }

    log('✨ Migration completed!', 'success');

    if (onProgress) {
      onProgress({ current: tests.length, total: tests.length, status: 'completed' });
    }

    if (onResult) {
      onResult(result);
    }

  } catch (error: any) {
    log(`❌ Migration failed: ${error.message}`, 'error');
    if (onProgress) {
      onProgress({ current: 0, total: 0, status: 'error' });
    }
    throw error;
  }

  return result;
};

// Export for window object (browser console access)
if (typeof window !== 'undefined') {
  (window as any).runTestMigration = runMigration;
}
